import { useState, useEffect, useCallback } from "react";
import {
  fetchMarketOverviewData, saveTypeOverride, clearTypeOverride, saveListingLink, clearListingLink,
} from "../../core/api/reportApi";
import { useAccount } from "../../core/hooks/useAccount";

// Loads a job's report data ONCE. Every report page (Market Overview,
// Advertiser Overview, PPC ...) calculates from this same data, so switching
// tabs, filters or the pin never fetches again. Type corrections and map pack
// listing links are the signed-in account's own; they're saved, then applied
// to the loaded data straight away, so every page updates at once.
export default function useReportData(jobId) {
  const account = useAccount();
  const agencyId = account ? account.agencyId : null;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    setLoading(true);
    setData(null);
    fetchMarketOverviewData(jobId, agencyId).then(loaded => {
      if (cancelled) return;
      setData(loaded);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [jobId, agencyId]);

  // type = a business type, or null to go back to AdAudit's own classification.
  // Returns an error message, or null.
  const setBusinessType = useCallback(async (key, type) => {
    const error = type ? await saveTypeOverride(key, type, agencyId) : await clearTypeOverride(key, agencyId);
    if (error) return error;
    setData(current => current && {
      ...current,
      typeOverrides: [...current.typeOverrides.filter(o => o.key !== key), ...(type ? [{ key, type }] : [])],
    });
    return null;
  }, [agencyId]);

  // A map pack listing with no website (listingKey "name:..."):
  //   action "link" + domain: joins that website
  //   action "unlink": a likely match is split back out
  //   action null: the link is removed (AdAudit's own matching again)
  // Returns an error message, or null.
  const setListingLink = useCallback(async (listingKey, action, domain = null) => {
    const error = action ? await saveListingLink(listingKey, action, domain, agencyId) : await clearListingLink(listingKey, agencyId);
    if (error) return error;
    setData(current => current && {
      ...current,
      listingLinks: [
        ...(current.listingLinks || []).filter(l => l.listing_key !== listingKey),
        ...(action ? [{ listing_key: listingKey, action, domain: action === "link" ? domain : null, status: "applied" }] : []),
      ],
    });
    return null;
  }, [agencyId]);

  return { data, loading, setBusinessType, setListingLink };
}
